import React from "react";
import { Link } from "react-router-dom";

import {
  Breadcrumbs,
  DemenagementLayout,
  DictimmoCta,
  InteractiveChecklist,
  InternalLinks,
  SourceList,
  itemListJsonLd,
  makeBreadcrumbs,
} from "./DemenagementCommon";

const path = "/checklists/checklist-visite-logement";
const breadcrumbs = makeBreadcrumbs([
  { name: "Déménagement", path: "/demenagement" },
  { name: "Checklist visite logement", path },
]);

const sources = [
  {
    label: "ANIL : questions à se poser avant d'acheter un logement",
    href: "https://www.anil.org/parole-expert-logement-questions-visite-acheter-logement-maison/",
  },
  {
    label: "Qualitel : checklist visite appartement",
    href: "https://www.qualitel.org/particuliers/visiter/checklist-visite-appartement/",
  },
];

const sections = [
  {
    title: "Adresse et quartier",
    items: [
      { id: "visite-adresse-exacte", label: "Noter l'adresse exacte et l'étage du logement" },
      { id: "visite-risques-dictimmo", label: "Vérifier l'adresse avec etatdulieu.com" },
      { id: "visite-prix-secteur", label: "-> Comparer avec les prix du secteur et les références proches" },
      { id: "visite-transports-services", label: "-> Observer transports, services, commerces et temps de trajet" },
      { id: "visite-environnement", label: "-> Vérifier les risques naturels et l'environnement immédiat" },
    ],
  },
  {
    title: "État général du logement",
    items: [
      { id: "visite-etat-murs", label: "Observer murs, sols, plafonds, portes et parties communes : pas de fissures, humidité ou dégâts !" },
      { id: "visite-travaux-recents", label: "Demander les travaux récents réalisés" },
      { id: "visite-travaux-prevus", label: "Demander les travaux prévus ou nécessaires" },
      { id: "visite-rangements", label: "Vérifier rangements, prises électriques et ethernet, ventilation et équipements inclus" },
    ],
  },
  {
    title: "Humidité, isolation, chauffage",
    items: [
      { id: "visite-humidite", label: "Chercher traces d'humidité, odeurs, moisissures ou peinture cloquée" },
      { id: "visite-fenetres", label: "Vérifier fenêtres, isolation et état des joints" },
      { id: "visite-chauffage", label: "Demander le type de chauffage et l'énergie utilisée" },
      { id: "visite-eau", label: "Tester la pression d'eau et l'eau chaude si possible" },
    ],
  },
  {
    title: "Bruit et luminosité",
    items: [
      { id: "visite-bruit-interieur", label: "Observer le bruit intérieur : voisins, parties communes, équipements" },
      { id: "visite-bruit-exterieur", label: "Observer le bruit extérieur : rue, transports, activités proches" },
      { id: "visite-luminosite", label: "Vérifier exposition, luminosité et vis-à-vis" },
      { id: "visite-horaires", label: "Prévoir une seconde visite à un autre horaire si le bien vous intéresse" },
      { id: "visite-bruit-voisins", label: "Demander si un voisin proche est musicien professionnel" },
    ],
  },
  {
    title: "Charges et dépenses",
    items: [
      { id: "visite-charges", label: "Demander le montant des charges et ce qu'elles incluent" },
      { id: "visite-energie-depenses", label: "Demander les dépenses d'énergie récentes si elles sont disponibles" },
      { id: "visite-taxe", label: "Pour un achat, vérifier les taxes et charges de copropriété" },
      { id: "visite-travaux-budget", label: "Identifier les travaux à prévoir dans votre budget" },
    ],
  },
  {
    title: "Diagnostics et documents",
    items: [
      { id: "visite-diagnostics", label: "Demander les diagnostics disponibles" },
      { id: "visite-dpe", label: "Lire le DPE et demander les précisions utiles" },
      { id: "visite-documents-bail", label: "Pour une location, demander les documents avant signature" },
      { id: "visite-internet", label: "Vérifier fibre/internet si la donnée est disponible sur etatdulieu.com ou directement auprès de l'opérateur" },
    ],
  },
  {
    title: "Copropriété ou immeuble",
    items: [
      { id: "visite-parties-communes", label: "Observer l'état des parties communes, cave, local vélo ou parking" },
      { id: "visite-ascenseur", label: "Vérifier ascenseur, accès, sécurité et interphone" },
      { id: "visite-copro-travaux", label: "Demander les travaux votés ou envisagés dans l'immeuble" },
      { id: "visite-voisinage", label: "Noter l'ambiance générale de l'immeuble" },
    ],
  },
  {
    title: "Questions à poser pendant la visite",
    items: [
      { id: "visite-disponibilite", label: "Quand le logement est-il disponible ?" },
      { id: "visite-raison", label: "Pourquoi le logement est-il mis en location ou en vente ?" },
      { id: "visite-equipements", label: "Quels équipements restent dans le logement ?" },
      { id: "visite-regles", label: "Quelles règles particulières s'appliquent à l'immeuble ou au bail ?" },
    ],
  },
  {
    title: "À vérifier après la visite",
    items: [
      { id: "visite-comparer", label: "Comparer le bien avec d'autres annonces du secteur" },
      { id: "visite-retour", label: "Lister les points à clarifier avant de déposer un dossier ou une offre" },
      { id: "visite-photos-notes", label: "Classer photos, notes, documents et questions restantes" },
    ],
  },
];

export default function ChecklistVisiteLogement() {
  const checklistJsonLd = itemListJsonLd({
    name: "Checklist visite logement avant location ou achat",
    description: "Liste de questions à poser et de points à vérifier pendant une visite de logement : adresse, quartier, humidité, isolation, chauffage, charges et diagnostics.",
    path,
    items: sections.map((section) => ({
      name: section.title,
      path,
    })),
  });

  return (
    <DemenagementLayout
      title="Checklist visite logement : questions avant location ou achat"
      description="Checklist visite logement à imprimer : questions avant de louer ou acheter, adresse, quartier, humidité, charges, chauffage et diagnostics."
      path={path}
      breadcrumbs={breadcrumbs}
      label="Checklist visite"
      printMode="document"
      jsonLd={[checklistJsonLd]}
    >
      <Breadcrumbs items={breadcrumbs} />

      <header className="move-tool-header">
        <h1>Checklist visite logement</h1>
        <p>
          Une visite passe vite. Cette checklist vous aide à noter les points
          concrets à observer sur place, puis à compléter votre impression avec
          une analyse de l'adresse.
        </p>
        <div className="move-actions no-print">
          <Link className="move-btn move-btn-secondary" to="/demenagement">
            Voir tous les outils déménagement
          </Link>
        </div>
      </header>

      <section className="move-note no-print" aria-labelledby="checklist-visite-seo-title">
        <h2 id="checklist-visite-seo-title">Avant de signer un bail ou de faire une offre</h2>
        <p>
          Cette checklist aide à répondre aux questions que l'on oublie souvent
          pendant une visite : que vérifier dans l'appartement, quels documents
          demander, comment comparer le quartier, et quels points clarifier avant
          de déposer un dossier de location ou une offre d'achat.
        </p>
      </section>

      <InteractiveChecklist
        title="Checklist visite logement"
        intro="Cochez pendant ou juste après la visite, puis imprimez la liste pour comparer plusieurs logements."
        sections={sections}
        printEventName="checklist_visite_printed"
      />

      <section className="move-note no-print" aria-labelledby="visite-guides-title">
        <h2 id="visite-guides-title">Compléter les notes de visite</h2>
        <p>
          Pour observer le cadre de vie, suivez la méthode pour <Link to="/guides/comment-verifier-un-quartier-avant-de-louer">vérifier un quartier avant de louer</Link>.
          {" "}Si les cartes signalent une exposition, lisez <Link to="/guides/verifier-les-risques-d-une-adresse">comment vérifier les risques d'une adresse</Link>.
        </p>
      </section>

      <DictimmoCta
        title="Ajoutez l'adresse à votre checklist"
        text="Lancez une analyse Dictimmo pour compléter votre visite avec les prix du secteur, les risques, l'environnement et les données de la commune."
        buttonLabel="Analyser cette adresse"
        eventName="dictimmo_cta_clicked_from_checklist_visite"
      />

      <InternalLinks currentPath={path} />
      <SourceList sources={sources} />
    </DemenagementLayout>
  );
}
